import React, { useEffect, useRef, useState } from 'react';
import styles from './ChangePassword.module.scss';
import { DocumentTitle, ErrorMessage, PasswordInput, SubmitButton } from '../../components';
import { InvalidTokenError } from '../../api/actions/changePassword/changePassword';
import { redirectTo } from '../../utils/utils';
import { UnifiedLayout } from '../../layouts/UnifiedLayout/UnifiedLayout';
import { Redirect, useParams } from 'react-router-dom';
import { Header, Divider, Form, Grid } from '@solarwindscloud/nighthawk-ui';
import { ROUTES } from '@solarwindscloud/sso-nighthawk-shared';
import { changePassword, validateResetToken } from '../../api/actions';
import { errorMessages } from '../../utils/translateErrorCode/translateErrorCode';

const ChangePassword = () => {
  const resetToken = useParams()?.token;
  const [validToken, setValidToken] = useState(!!resetToken);
  const [passwordError, setPasswordError] = useState('');
  const [passwordConfirmationError, setPasswordConfirmationError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inputsEmpty, setInputsEmpty] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);

  const passwordInput = useRef(null);
  const passwordConfirmationInput = useRef(null);

  useEffect(() => {
    if (resetToken) {
      validateResetToken(resetToken)
        .then(() => {
          setValidToken(true);
          setPageLoading(false);
        })
        .catch(() => {
          setValidToken(false);
        });
    }
  }, [resetToken]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const data = { password: getPassword(), resetToken: resetToken };
      await changePassword(data);
      redirectToSuccessPage();
    } catch (error) {
      setLoading(false);
      if (error instanceof InvalidTokenError) {
        setValidToken(false);
      } else {
        setGeneralError(errorMessages.internal_server_error);
      }
    }
  };

  const redirectToSuccessPage = () => {
    redirectTo(ROUTES.PASSWORD_CHANGED);
  };

  const redirectToResetPasswordWithError = () => {
    return (
      <Redirect
        to={{
          pathname: ROUTES.RESET_PASSWORD,
          state: {
            invalidResetPasswordToken: true,
          },
        }}
      />
    );
  };

  const hasErrors = () => {
    return !!(passwordError || passwordConfirmationError || generalError);
  };

  const getPassword = () => {
    return passwordInput?.current?.inputRef?.current?.value || '';
  };

  const getPasswordConfirmation = () => {
    return passwordConfirmationInput?.current?.inputRef?.current?.value || '';
  };

  const handlePasswordChange = () => {
    setPasswordError('');
    handleChange();
  };

  const handleChange = () => {
    setGeneralError('');
    const passwordsMatch = getPassword() === getPasswordConfirmation();
    if (getPasswordConfirmation() !== '' && !passwordsMatch) {
      setPasswordConfirmationError('Passwords do not match');
    } else if (passwordsMatch) {
      setPasswordConfirmationError('');
    }
    setInputsEmpty(!getPassword() || !getPasswordConfirmation());
  };

  const renderPasswordInput = () => (
    // make component uncontrolled to avoid exposing value by "inspect element"
    <PasswordInput
      ref={passwordInput}
      onChange={handlePasswordChange}
      hasError={!!passwordError}
      onError={setPasswordError}
      required
      data-test-password-input
    />
  );

  const renderPasswordConfirmationInput = () => (
    // make component uncontrolled to avoid exposing value by "inspect element"
    <PasswordInput
      ref={passwordConfirmationInput}
      onChange={handleChange}
      hasError={!!passwordConfirmationError}
      errorMessage={passwordConfirmationError}
      required
      placeholder="Confirm password"
      name="passwordConfirmation"
      label="Confirm password"
      data-test-confirm-password-input
    />
  );

  const renderErrorMessage = () => {
    return generalError && <ErrorMessage message={generalError} renderCloseMessage />;
  };
  const renderFormBody = () => {
    return (
      <Form className={styles.changePassForm} onSubmit={handleSubmit} method="POST" error={hasErrors()} data-test-form>
        <Grid verticalAlign="middle">
          <Grid.Column width={12}>
            {renderPasswordInput()}
            {renderPasswordConfirmationInput()}
          </Grid.Column>
          <Grid.Column width={12}>
            <SubmitButton disabled={inputsEmpty || hasErrors() || loading} loading={loading}>
              Save new password
            </SubmitButton>
          </Grid.Column>
        </Grid>
      </Form>
    );
  };

  const renderHeader = () => {
    return (
      <Header textAlign="center">
        <Header.Subheader>
          Password must be at least 8 characters long,
          <br />
          at least one uppercase character, lowercase character, number, and symbol.
        </Header.Subheader>
      </Header>
    );
  };

  if (!validToken) {
    return redirectToResetPasswordWithError();
  }
  if (pageLoading) {
    return null;
  }
  return (
    <DocumentTitle title={'SolarWinds Cloud'}>
      <UnifiedLayout headerText="Enter your new password" dataTestPageName="ChangePassword">
        {renderHeader()}
        {renderFormBody()}
        <Divider hidden section />
        {renderErrorMessage()}
      </UnifiedLayout>
    </DocumentTitle>
  );
};
export default ChangePassword;
